/**
 * Unités canoniques (§3.2 CLAUDE.md) : catégories, table d'alias pour la
 * normalisation à l'entrée, et helpers numériques partagés par le parseur
 * (parseIngredientLine.ts) et le moteur d'adaptation (adaptQuantities.ts).
 *
 * Décision d'arbitrage (cf. DECISIONS.md, « Types partagés ») : les unités
 * anglo-saxonnes (cup, oz, lb, tsp, tbsp) sont des unités canoniques à part
 * entière, au même titre que g/kg ou ml/cl/l/c. à c./c. à s. Le parseur ne les
 * convertit PAS en unité métrique : « 1 cup » reste stocké comme
 * quantity=1, unit='cup'. Les équivalences données par le brief (cup = 240 ml,
 * oz = 28,35 g, lb = 453,6 g) sont une information de référence pour un usage
 * futur (ex. fusion de listes de courses en V2), pas une règle de conversion
 * appliquée à la saisie.
 */

import { CANONICAL_UNITS, type Unit } from '@mlc/shared';

export type UnitCategory = 'masse' | 'volume' | 'denombre' | 'domestique' | 'anglo';

/** Catégorie de chaque unité canonique (§3.2). Vérifié exhaustif à la compilation. */
export const UNIT_CATEGORY = {
  g: 'masse',
  kg: 'masse',

  ml: 'volume',
  cl: 'volume',
  l: 'volume',
  'c. à c.': 'volume',
  'c. à s.': 'volume',

  piece: 'denombre',
  pincee: 'denombre',
  botte: 'denombre',
  gousse: 'denombre',
  brin: 'denombre',
  tranche: 'denombre',

  // Domestiques (US-26) : sans équivalent métrique fixe, elles se comportent
  // comme des dénombrées à l'adaptation — un demi-pot de sucre est une mesure
  // qu'on sait faire, 0,63 pot non.
  pot: 'domestique',
  sachet: 'domestique',
  verre: 'domestique',
  cuillere: 'domestique',
  bol: 'domestique',
  noix: 'domestique',

  cup: 'anglo',
  oz: 'anglo',
  lb: 'anglo',
  tsp: 'anglo',
  tbsp: 'anglo',
} as const satisfies Record<Unit, UnitCategory>;

/**
 * Ingrédients dénombrables (§3.1) : arrondis au demi le plus proche lors de
 * l'adaptation. Les unités domestiques (US-26) en font partie — on double une
 * recette de gâteau au yaourt en passant de 2 à 4 pots de sucre, jamais à
 * 4,17 pots.
 */
export function isCountableUnit(unit: Unit): boolean {
  const category = UNIT_CATEGORY[unit];
  return category === 'denombre' || category === 'domestique';
}

/**
 * Unités « petites mesures » pour lesquelles l'arrondi d'affichage (§3.1)
 * privilégie une fraction usuelle (⅓, ½, ⅔…) plutôt qu'un nombre décimal.
 * Volontairement limité aux cuillères et aux mesures anglo-saxonnes de volume
 * (cup, tsp, tbsp) : g/kg/ml/cl/l affichent toujours un nombre.
 */
export const FRACTION_DISPLAY_UNITS: ReadonlySet<Unit> = new Set(['c. à c.', 'c. à s.', 'cup', 'tsp', 'tbsp']);

/** Retire les diacritiques et les points, pour comparer des écritures équivalentes d'une même unité. */
const COMBINING_DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

export function normalizeForMatch(value: string): string {
  return value
    .normalize('NFD')
    .replace(COMBINING_DIACRITICS, '')
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Table d'alias : toute écriture rencontrée à l'import est convertie vers
 * l'unité canonique correspondante (§3.2 : « toute autre écriture est
 * convertie à l'entrée »). Clés déjà normalisées via `normalizeForMatch`.
 */
const UNIT_ALIAS_ENTRIES: Array<[string, Unit]> = [
  // masse
  ['g', 'g'],
  ['gr', 'g'],
  ['gramme', 'g'],
  ['grammes', 'g'],
  ['kg', 'kg'],
  ['kilo', 'kg'],
  ['kilos', 'kg'],
  ['kilogramme', 'kg'],
  ['kilogrammes', 'kg'],

  // volume
  ['ml', 'ml'],
  ['millilitre', 'ml'],
  ['millilitres', 'ml'],
  ['cl', 'cl'],
  ['centilitre', 'cl'],
  ['centilitres', 'cl'],
  ['l', 'l'],
  ['litre', 'l'],
  ['litres', 'l'],
  ['c a cafe', 'c. à c.'],
  ['c a c', 'c. à c.'],
  ['cac', 'c. à c.'],
  ['cuillere a cafe', 'c. à c.'],
  ['cuilleres a cafe', 'c. à c.'],
  ['cuil a cafe', 'c. à c.'],
  ['c a soupe', 'c. à s.'],
  ['c a s', 'c. à s.'],
  ['cas', 'c. à s.'],
  ['cuillere a soupe', 'c. à s.'],
  ['cuilleres a soupe', 'c. à s.'],
  ['cuil a soupe', 'c. à s.'],

  // dénombré
  ['piece', 'piece'],
  ['pieces', 'piece'],
  ['unite', 'piece'],
  ['unites', 'piece'],
  ['pincee', 'pincee'],
  ['pincees', 'pincee'],
  ['botte', 'botte'],
  ['bottes', 'botte'],
  ['gousse', 'gousse'],
  ['gousses', 'gousse'],
  // Le §3.1 cite « feuilles de laurier » parmi les ingrédients dénombrables :
  // « feuille » n'est pas une unité canonique, on la range sous `piece` plutôt
  // que d'élargir la liste fermée du §3.2.
  ['feuille', 'piece'],
  ['feuilles', 'piece'],
  ['brin', 'brin'],
  ['brins', 'brin'],
  ['tranche', 'tranche'],
  ['tranches', 'tranche'],

  // domestique (US-26). « cuillere » nue est bien la cuillère de maison ; les
  // alias plus longs « cuillere a soupe » et « cuillere a cafe » restent des
  // mesures de volume définies, et l'appariement retient le plus long (voir
  // matchLeadingUnit.ts).
  ['pot', 'pot'],
  ['pots', 'pot'],
  ['sachet', 'sachet'],
  ['sachets', 'sachet'],
  ['verre', 'verre'],
  ['verres', 'verre'],
  ['cuillere', 'cuillere'],
  ['cuilleres', 'cuillere'],
  ['bol', 'bol'],
  ['bols', 'bol'],
  ['noix', 'noix'],

  // anglo
  ['cup', 'cup'],
  ['cups', 'cup'],
  ['tasse', 'cup'],
  ['tasses', 'cup'],
  ['oz', 'oz'],
  ['once', 'oz'],
  ['onces', 'oz'],
  ['ounce', 'oz'],
  ['ounces', 'oz'],
  ['lb', 'lb'],
  ['lbs', 'lb'],
  ['livre', 'lb'],
  ['livres', 'lb'],
  ['tsp', 'tsp'],
  ['teaspoon', 'tsp'],
  ['teaspoons', 'tsp'],
  ['tbsp', 'tbsp'],
  ['tablespoon', 'tbsp'],
  ['tablespoons', 'tbsp'],
];

export const UNIT_ALIASES: ReadonlyMap<string, Unit> = new Map(UNIT_ALIAS_ENTRIES);

/**
 * Premiers mots des alias en plusieurs mots (« c », « cuillere », « cuil »…).
 *
 * Sert de garde au parseur : une ligne comme « 2 cuillères bombées de sucre »
 * commence par un mot d'unité sans correspondre à aucun alias complet. Dans ce
 * cas on applique le repli du §3.2 au lieu de supposer `piece` — c'est une
 * unité mal reconnue, pas un compte d'unités entières.
 */
export const UNIT_ALIAS_HEADS: ReadonlySet<string> = new Set(
  UNIT_ALIAS_ENTRIES.map(([alias]) => alias)
    .filter((alias) => alias.includes(' '))
    .map((alias) => alias.split(' ')[0] ?? ''),
);

/**
 * Unités **ambiguës** : un alias complet à elles seules, et en même temps le
 * début d'un alias plus long. « cuillere » en est la seule (US-26) — elle
 * désigne la cuillère de maison, mais commence aussi « cuillere a soupe » et
 * « cuillere a cafe », qui sont des volumes définis.
 *
 * Conséquence : « 2 cuillères bombées de sucre » ne doit pas devenir
 * « 2 cuillere » de « bombées de sucre ». Une unité ambiguë n'est retenue que
 * si un mot de liaison la suit (« de », « d' », « du », « des ») — sinon le
 * repli du §3.2 s'applique et la ligne est conservée entière.
 */
export const AMBIGUOUS_UNITS: ReadonlySet<Unit> = new Set(
  [...UNIT_ALIASES.entries()]
    .filter(([alias]) => UNIT_ALIAS_HEADS.has(alias))
    .map(([, unit]) => unit),
);

/** Mots de liaison qui séparent une unité de l'ingrédient qu'elle mesure. */
export const LIAISON_WORDS: ReadonlySet<string> = new Set(['de', "d'", 'd’', 'du', 'des', "l'", 'l’']);

/** Garde-fou de compilation : chaque unité canonique a au moins un alias. */
export function assertAllUnitsHaveAlias(): void {
  const covered = new Set(UNIT_ALIASES.values());
  for (const unit of CANONICAL_UNITS) {
    if (!covered.has(unit)) {
      throw new Error(`Unité canonique sans alias de parsing : ${unit}`);
    }
  }
}
